// Fonte única (leaf module, sem dependências pesadas) da lógica de CNPJ.
// Suporta o formato numérico legado e o alfanumérico da Receita Federal (jul/2026):
// 14 posições, dígitos 0-9 e letras A-Z, com os 2 DVs sempre numéricos. O checksum é
// módulo 11 com os caracteres convertidos pelo valor ASCII − 48 (0-9 → 0-9, A-Z → 17-42),
// idêntico ao módulo 11 base 10 quando o input é numérico — regressão zero.

const CNPJ_LENGTH = 14;
const CNPJ_ALPHANUMERIC_REGEX = /^[0-9A-Z]{14}$/;
const CNPJ_REPEATED_CHAR_REGEX = /^(.)\1{13}$/;
const CNPJ_WEIGHTS_FIRST_DV = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_WEIGHTS_SECOND_DV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const CNPJ_ASCII_OFFSET = 48;
const CNPJ_DIGIT_FIRST_INDEX = 12;
const CNPJ_DIGIT_SECOND_INDEX = 13;
const CNPJ_MOD = 11;
const CNPJ_MIN_MOD = 2;

// CNPJ alfanumérico (RFB jul/2026): letras nas posições de dados, DV (2 últimos) numérico.
export const cnpjRegex =
	/[0-9A-Za-z]{2}[\.]?[0-9A-Za-z]{3}[\.]?[0-9A-Za-z]{3}[\/]?[0-9A-Za-z]{4}[-]?\d{2}/;

/**
 * Remove separadores ( ./- ), faz trim e normaliza para UPPERCASE — preservando
 * letras para suportar o CNPJ alfanumérico da Receita Federal.
 */
export function sanitizeCnpj(cnpj: string): string {
	return cnpj.toString().trim().toUpperCase().replace(/[./-]/g, "");
}

/**
 * Converte um caractere pelo valor ASCII subtraído de 48 (regra da RFB):
 * 0-9 → 0-9, A-Z → 17-42. Retorna NaN para qualquer outro caractere.
 */
function cnpjCharValue(char: string): number {
	const code = char.charCodeAt(0);
	if (code >= 48 && code <= 57) return code - CNPJ_ASCII_OFFSET;
	if (code >= 65 && code <= 90) return code - CNPJ_ASCII_OFFSET;
	return Number.NaN;
}

function cnpjCheckDigit(payload: string, weights: number[]): number {
	const sum = payload
		.split("")
		.reduce((acc, char, index) => acc + cnpjCharValue(char) * weights[index], 0);
	const mod = sum % CNPJ_MOD;
	return mod < CNPJ_MIN_MOD ? 0 : CNPJ_MOD - mod;
}

/**
 * Valida CNPJ numérico (legado) ou alfanumérico (RFB jul/2026) via módulo 11 com
 * valores ASCII−48. Numérico mapeia idêntico ao algoritmo anterior — regressão zero.
 */
export function validateCnpj(cnpj: string): boolean {
	const cleaned = sanitizeCnpj(cnpj);

	if (
		cleaned.length !== CNPJ_LENGTH ||
		!CNPJ_ALPHANUMERIC_REGEX.test(cleaned) ||
		CNPJ_REPEATED_CHAR_REGEX.test(cleaned)
	) {
		return false;
	}

	const firstDigit = cnpjCheckDigit(
		cleaned.substring(0, CNPJ_DIGIT_FIRST_INDEX),
		CNPJ_WEIGHTS_FIRST_DV,
	);
	if (Number(cleaned[CNPJ_DIGIT_FIRST_INDEX]) !== firstDigit) {
		return false;
	}

	const secondDigit = cnpjCheckDigit(
		cleaned.substring(0, CNPJ_DIGIT_SECOND_INDEX),
		CNPJ_WEIGHTS_SECOND_DV,
	);
	return Number(cleaned[CNPJ_DIGIT_SECOND_INDEX]) === secondDigit;
}
